import { useCallback, useMemo } from 'react'
import { useBoolean } from './use-boolean'
import { AnyPlaybookType } from 'app/types'
import { isQG } from 'modules'
import { generatePath, useHistory } from 'react-router-dom'
import { paths } from 'app/paths'

const getBrowserLanguages = () => {
    if (!navigator) {
        return []
    }

    return navigator.languages || (navigator.language ? [navigator.language] : [])
}

const findBestMatchingLanguageCode = (availableTranslations: string[]) => {
    const browserLanguages = getBrowserLanguages()

    if (!availableTranslations?.length || !browserLanguages?.length) {
        return null
    }

    const exactMatch = availableTranslations.find(lang => browserLanguages.includes(lang))

    if (exactMatch) {
        return exactMatch
    }

    const baseMatch = availableTranslations.find(lang => {
        return browserLanguages.find(browserLang => {
            const baseLang = browserLang.split('-')[0].toLowerCase()

            return lang.toLowerCase().includes(baseLang)
        })
    })

    return baseMatch || null
}

export const useBrowserLanguagePreferences = () => {
    const wasInUse = useBoolean()

    const history = useHistory()

    const historyPush = history.push

    const openPlaybook = useCallback(
        (playbookId: string) => {
            const path = generatePath(paths.playbookDetails, { playbookId })
            const fullPath = location.search ? `${path}${location.search}` : path
            historyPush(fullPath)
        },
        [historyPush],
    )
    const setWasInUse = wasInUse.setTrue
    const openPreferredLanguage = useCallback(
        (playbook: AnyPlaybookType) => {
            if (wasInUse.isTrue || !isQG(playbook)) {
                return
            }

            const translations = playbook?.translations

            if (!translations?.length) {
                return
            }

            const langCodes = translations.map(t => t.language.langCode)
            const preferredCode = findBestMatchingLanguageCode(langCodes)

            const playbookWithPreferredLanguage = translations.find(t => t.language.langCode === preferredCode)

            if (!playbookWithPreferredLanguage) {
                return
            }

            openPlaybook(playbookWithPreferredLanguage.id)
            setWasInUse()
        },
        [openPlaybook, setWasInUse, wasInUse.isTrue],
    )

    return useMemo(
        () => ({
            openPreferredLanguage,
        }),
        [openPreferredLanguage],
    )
}
